import React from "react";
import { Link, NavLink } from "react-router-dom";
import { FiX, FiLogOut } from "react-icons/fi";
import { useStateContext } from "../contexts/ContextProvider";
import { navGroups } from "./navigation";
import Logo from "../data/bookwie-logo.png";
import Icon from "../data/bookwie-icon.png";

// `collapsed` renders the desktop icon rail; labels move to native tooltips
const Sidebar = ({ collapsed = false }) => {
  const { setActiveMenu, screenSize, logout } = useStateContext();

  const handleCloseSidebar = () => {
    if (screenSize <= 900) setActiveMenu(false);
  };

  const shape = collapsed ? "justify-center w-11 h-11 mx-auto" : "gap-3 px-4 py-2";

  const linkClass = ({ isActive }) =>
    `flex items-center rounded-full text-sm transition-colors ${shape} ${
      isActive
        ? "bg-brand-600 text-white font-medium shadow-sm"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`;

  return (
    <aside className="h-full flex flex-col bg-white rounded-3xl shadow-card overflow-hidden">
      <div className={`flex items-center pt-6 pb-3 ${collapsed ? "justify-center" : "justify-between px-6"}`}>
        <Link to="/dashboard" onClick={handleCloseSidebar} className="flex items-center gap-2.5" title={collapsed ? "Bookwie Admin" : undefined}>
          {collapsed ? (
            <img src={Icon} alt="Bookwie" className="w-9 h-9 rounded-xl" />
          ) : (
            <>
              <img src={Logo} alt="Bookwie" className="h-6 w-auto" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-700 bg-brand-100 rounded-full px-2 py-0.5">
                Admin
              </span>
            </>
          )}
        </Link>
        {!collapsed && (
          <button
            type="button"
            onClick={() => setActiveMenu(false)}
            className="icon-btn md:hidden"
            aria-label="Close menu"
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      <nav className={`flex-1 overflow-y-auto scrollbar-thin pb-4 ${collapsed ? "px-2" : "px-3"}`}>
        {navGroups.map((group, index) => (
          <div key={group.title} className={collapsed ? "" : "mt-3 first:mt-0"}>
            {collapsed ? (
              index > 0 && <div className="mx-auto my-2 w-6 border-t border-gray-200" />
            ) : (
              <p className="px-4 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {group.title}
              </p>
            )}
            <div className={collapsed ? "space-y-1" : "space-y-0.5"}>
              {group.items.map(({ to, label, icon: ItemIcon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={handleCloseSidebar}
                  className={linkClass}
                  title={collapsed ? label : undefined}
                  aria-label={collapsed ? label : undefined}
                >
                  <ItemIcon size={collapsed ? 18 : 17} className="shrink-0" />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className={`border-t border-gray-100 ${collapsed ? "py-3" : "p-3"}`}>
        <button
          type="button"
          onClick={logout}
          title={collapsed ? "Log out" : undefined}
          aria-label={collapsed ? "Log out" : undefined}
          className={`flex items-center rounded-full text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors ${
            collapsed ? shape : "w-full gap-3 px-4 py-2.5"
          }`}
        >
          <FiLogOut size={17} />
          {!collapsed && <span>Log out</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
